// @ts-strict-ignore
import { breakpoint } from '@carvana/helios-styles';
import classnames from 'classnames';
import Image from 'next/image';

import type { VehicleInfo } from '../../../../types/vehicleInfo';
import VehicleEntry from '../VehicleEntry';
import {
  Background,
  Card,
  CardWrapper,
  LeftColumnWrapper,
  Heading,
  Subheading,
  HeroContainer,
  ImageStyle,
  RightColumnWrapper,
  VectorWrap,
  VehicleImageWrapper
} from './NewHero.styles';

const Vector = ({ gradientId }: { gradientId: string }) => (
  <>
    {/* Mobile vector: < 440px */}
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      height={188}
      viewBox="0 0 807 188"
      preserveAspectRatio="xMidYMid slice"
      fill="none"
      className="vector-xs"
    >
      <path
        d="M20 148.375H262.717C265.088 148.375 267.31 147.218 268.669 145.276L339.666 43.835C342.525 39.7501 348.555 39.6926 351.491 43.7222L433.336 156.041C436.321 160.137 442.476 159.996 445.27 155.767L532.821 23.2604C534.166 21.2247 536.443 20 538.883 20H787"
        stroke={`url(#${gradientId}-xs)`}
        strokeWidth={40}
        strokeLinecap="square"
      />
      <defs>
        <linearGradient
          id={`${gradientId}-xs`}
          x1={238.817}
          y1={79.1634}
          x2={627.5}
          y2={79.1634}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#004C8F" />
          <stop offset={0.238209} stopColor="#106BC7" />
          <stop offset={0.468425} stopColor="#228BE6" />
          <stop offset={0.718625} stopColor="#3BC9DB" />
          <stop offset={1} stopColor="#99E9F2" stopOpacity={0.4} />
        </linearGradient>
      </defs>
    </svg>

    {/* Tablet vector: 440px - 1280px */}
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      height={280}
      viewBox="0 0 1500 500"
      preserveAspectRatio="xMidYMid slice"
      fill="none"
      className="vector-sm"
    >
      <path
        d="M1453.5 196.349H964.839C952.415 196.349 941.016 203.237 935.238 214.235L816.223 440.772C803.67 464.666 769.441 464.608 756.969 440.671L558.877 60.488C545.882 35.5466 509.774 36.8141 498.56 62.6053L369.402 359.638C364.094 371.846 352.05 379.742 338.738 379.742H42.5"
        stroke={`url(#${gradientId}-sm)`}
        strokeWidth={85}
        strokeLinecap="square"
      />
      <defs>
        <linearGradient
          id={`${gradientId}-sm`}
          x1={-54.5004}
          y1={337.349}
          x2={1119.5}
          y2={321.349}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#004C8F" />
          <stop offset={0.238209} stopColor="#106BC7" />
          <stop offset={0.468425} stopColor="#228BE6" />
          <stop offset={0.718625} stopColor="#3BC9DB" />
          <stop offset={1} stopColor="#99E9F2" stopOpacity={0.4} />
        </linearGradient>
      </defs>
    </svg>

    {/* Desktop vector: >= 1280px */}
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      height={322}
      viewBox="0 0 2299 502"
      preserveAspectRatio="xMidYMid slice"
      fill="none"
      className="vector-lg"
    >
      <path
        stroke={`url(#${gradientId}-lg)`}
        strokeLinecap="square"
        strokeWidth={85}
        d="M42.5 379.742h1099.24c13.31 0 25.35-7.896 30.66-20.104l129.16-297.033c11.21-25.791 47.32-27.058 60.32-2.117l198.09 380.183c12.47 23.937 46.7 23.995 59.25.101l119.02-226.537a33.442 33.442 0 0 1 29.6-17.886h488.66"
      />
      <defs>
        <linearGradient
          id={`${gradientId}-lg`}
          x1={748.5}
          x2={2048}
          y1={337.349}
          y2={337.349}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#004C8F" />
          <stop offset={0.238} stopColor="#106BC7" />
          <stop offset={0.468} stopColor="#228BE6" />
          <stop offset={0.719} stopColor="#3BC9DB" />
          <stop offset={1} stopColor="#99E9F2" stopOpacity={0.4} />
        </linearGradient>
      </defs>
    </svg>

    {/* XL Desktop vector: >= 1440px */}
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      height={500}
      viewBox="0 0 2299 502"
      preserveAspectRatio="xMidYMid slice"
      fill="none"
      className="vector-xl"
    >
      <path
        stroke={`url(#${gradientId}-xl)`}
        strokeLinecap="square"
        strokeWidth={85}
        d="M42.5 379.742h1099.24c13.31 0 25.35-7.896 30.66-20.104l129.16-297.033c11.21-25.791 47.32-27.058 60.32-2.117l198.09 380.183c12.47 23.937 46.7 23.995 59.25.101l119.02-226.537a33.442 33.442 0 0 1 29.6-17.886h488.66"
      />
      <defs>
        <linearGradient
          id={`${gradientId}-xl`}
          x1={748.5}
          x2={2048}
          y1={337.349}
          y2={337.349}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#004C8F" />
          <stop offset={0.238} stopColor="#106BC7" />
          <stop offset={0.468} stopColor="#228BE6" />
          <stop offset={0.719} stopColor="#3BC9DB" />
          <stop offset={1} stopColor="#99E9F2" stopOpacity={0.4} />
        </linearGradient>
      </defs>
    </svg>
  </>
);

const NewHero = ({ customVehicleInfo }: { customVehicleInfo: VehicleInfo }) => {
  const defaultVehicleInfo: VehicleInfo = {
    vehicleYear: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleDisplayName: 'Car',
    vehicleDesktopImageUrl: '',
    vehicleMobileImageUrl: ''
  };

  const { vehicleDisplayName, vehicleDesktopImageUrl, vehicleMobileImageUrl } =
    customVehicleInfo || defaultVehicleInfo;

  const noImage = !vehicleDesktopImageUrl && !vehicleMobileImageUrl;

  const mobileDesktopImageLoader = ({ width }) =>
    width <= parseInt(breakpoint.xl, 10) ? vehicleMobileImageUrl : vehicleDesktopImageUrl;

  return (
    <Background>
      <VectorWrap className={classnames({ 'no-image': noImage })}>
        <Vector gradientId="big" />
      </VectorWrap>
      <HeroContainer>
        <LeftColumnWrapper>
          <Heading>Get your {vehicleDisplayName}'s Value</Heading>
          <Subheading>Know what your car is worth and track value changes over time.</Subheading>
        </LeftColumnWrapper>
        <RightColumnWrapper>
          {noImage ? null : (
            <VehicleImageWrapper>
              <Image
                src={vehicleDesktopImageUrl}
                loader={mobileDesktopImageLoader}
                style={ImageStyle}
                fill
                sizes="320px"
                alt="vehicle image"
              />
            </VehicleImageWrapper>
          )}
          <CardWrapper className={classnames({ 'no-image': noImage })}>
            <Card>
              <VehicleEntry />
            </Card>
          </CardWrapper>
        </RightColumnWrapper>
      </HeroContainer>
    </Background>
  );
};

export default NewHero;
